import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import clsx from 'clsx';
import _uniqBy from 'lodash/uniqBy';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { ColoredBackground } from 'reaxl';

import queryDuck from '@/ducks/queryDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpSpotlightDuck from '@/ducks/srp/srpSpotlightDuck.js';
import srpSupplementalDuck from '@/ducks/srp/srpSupplementalDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import alphaModule from '@/modules/AlphaModule';

import CompareDrawerPlaceholder from '@/components/CompareDrawerPlaceholder';
import LazyComponent from '@/components/LazyComponent';
import SearchLink from '@/components/SearchLink';
import VdpPaginationButtons from '@/components/vehicledetails/VdpPaginationButtons';

const CompareListingsDrawer = dynamic(() => import(
    /* webpackChunkName: "CompareListingsDrawer" */
    '@/components/CompareListingsDrawer'
), {
    loading: () => (<CompareDrawerPlaceholder />),
    ssr: false,
});

const CompareListingsDrawerMobile = dynamic(() => import(
    /* webpackChunkName: "CompareListingsDrawer" */
    '@/components/CompareListingsDrawer'
), {
    loading: () => (<CompareDrawerPlaceholder isCollapsed />),
    ssr: false,
});

// wrapStyle support super small device, so that the user has to side scroll
const wrapStyle = {
    overflowX: 'auto',
};

export function VdpPaginationContainer() {
    const {
        compare: [enableCompare],
    } = useFeatures([
        'compare',
    ]);

    const device = useDevice();
    const isXs = device.mediaType === 'xs';

    const { brand, brands, isBrand } = useBrand();

    const { id: listingId } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const compareToggle = useSelector(userPreferencesDuck.selectors.getCompareToggle);

    const query = useSelector(queryDuck.selectors.getDuckState);
    const resultCount = useSelector(srpResultsDuck.selectors.getResultsCount);

    const inventoryListings = useSelector(srpResultsDuck.selectors.getActiveInventory);
    const { listings: alphaListingIds } = useSelector(alphaModule.duck.selectors.getDuckState);
    const spotlightListingIds = useSelector(srpSpotlightDuck.selectors.getActiveResults);
    const supplementalListingIds = useSelector(srpSupplementalDuck.selectors.getActiveResults);

    let alphaListing;
    if (alphaListingIds && alphaListingIds.length > 0) {
        alphaListing = { listingId: alphaListingIds[0], clickType: 'alpha' };
    }
    const paginationListingIds = [
        alphaListing,
        ...spotlightListingIds.map((id) => ({ listingId: id, clickType: 'spotlight' })),
        ...inventoryListings.map(({ id }) => ({ listingId: id, clickType: 'listing' })),
        ...supplementalListingIds.map((id) => ({ listingId: id, clickType: 'supplemental' })),
    ].filter(Boolean);

    const listingIds = _uniqBy(paginationListingIds, 'listingId');

    const [showCompareDrawer, setShowCompareDrawer] = useState(false);

    useEffect(() => {
        if (enableCompare) {
            setShowCompareDrawer(compareToggle);
        }
    }, [enableCompare, compareToggle]);

    return (
        <>
            <ColoredBackground
                className="container-fluid"
                style={wrapStyle}
            >
                <div
                    className={clsx('container', {
                        'margin-top-4': isBrand(brands.KBB_BRAND),
                    })}
                >
                    <div
                        className="align-items-center display-flex justify-content-between"
                    >
                        <SearchLink />
                        {query?.clickType !== 'similar' && resultCount > 0 && listingIds && listingIds.length > 0 && (
                            <VdpPaginationButtons
                                channel={brand}
                                listingId={listingId}
                                listingIds={listingIds || []}
                                query={query}
                            />
                        )}
                    </div>
                </div>
            </ColoredBackground>
            {showCompareDrawer && isXs && (
                <LazyComponent
                    key="compare-drawer-mobile"
                    fallback={<CompareDrawerPlaceholder isCollapsed />}
                >
                    <CompareListingsDrawerMobile
                        isCollapsible
                        parent="vdp-paginate"
                    />
                </LazyComponent>
            )}
            {showCompareDrawer && !isXs && (
                <LazyComponent
                    key="compare-drawer-desktop"
                    fallback={<CompareDrawerPlaceholder />}
                >
                    <CompareListingsDrawer
                        isCollapsible
                        parent="vdp-paginate"
                    />
                </LazyComponent>
            )}
        </>
    );
}

export default VdpPaginationContainer;
